import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { MiniNavbar } from "@/components/ui/mini-navbar";
import { Footer } from "@/components/landing/Footer";

const COMPANY = "MR Dula Enterprise, LLC";
const PRODUCT = "OpenGateway Hosted";
const SITE = "https://opengateways.xyz";
const CONTACT = "matt@mrdula.solutions";
const EFFECTIVE = "August 13, 2026";

function LegalShell({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="min-h-screen bg-og-ink text-white">
      <MiniNavbar />
      <main className="mx-auto max-w-3xl px-4 pb-24 pt-28 md:px-6">
        <p className="text-sm font-medium text-orange-400/90">Legal</p>
        <h1 className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl">
          {title}
        </h1>
        <p className="mt-2 text-sm text-zinc-500">
          Effective date: {EFFECTIVE} · {COMPANY}
        </p>
        <div className="prose-legal mt-8 space-y-6 text-[15px] leading-relaxed text-zinc-300">
          {children}
        </div>
        <p className="mt-10 text-sm text-zinc-500">
          Questions?{" "}
          <a
            href={`mailto:${CONTACT}`}
            className="text-orange-300 underline-offset-2 hover:underline"
          >
            {CONTACT}
          </a>
        </p>
        <Link
          to="/"
          className="mt-8 inline-flex text-sm font-medium text-zinc-300 transition hover:text-white"
        >
          ← Back home
        </Link>
      </main>
      <Footer />
    </div>
  );
}

function H({ children }: { children: ReactNode }) {
  return (
    <h2 className="text-lg font-semibold tracking-tight text-white">{children}</h2>
  );
}

function P({ children }: { children: ReactNode }) {
  return <p className="text-zinc-400">{children}</p>;
}

function Ul({ items }: { items: string[] }) {
  return (
    <ul className="list-disc space-y-1.5 pl-5 text-zinc-400">
      {items.map((t) => (
        <li key={t}>{t}</li>
      ))}
    </ul>
  );
}

/** Production Privacy Policy for OpenGateway Hosted (SaaS). */
export function PrivacyPage() {
  return (
    <LegalShell title="Privacy Policy">
      <P>
        This Privacy Policy explains how {COMPANY} (“we”, “us”) collects, uses,
        and shares information when you use {PRODUCT} at {SITE}, Command, and
        related multi-tenant hubs (the “Service”). It does not apply to the
        open-source OpenGateway software you self-host.
      </P>

      <H>1. Who we are</H>
      <P>
        The Service is operated by {COMPANY}. Contact:{" "}
        <a className="text-orange-300 hover:underline" href={`mailto:${CONTACT}`}>
          {CONTACT}
        </a>
        .
      </P>

      <H>2. Information we collect</H>
      <Ul
        items={[
          "Account data from our authentication provider (Clerk): name, email, organization membership, and authentication metadata.",
          "Billing data processed by Stripe and/or Clerk Billing: plan, subscription status, and payment identifiers. We do not store full card numbers.",
          "Service data you and your agents create on the hub: rooms, messages, tasks, artifacts, device API keys (hashed), and workspace files.",
          "Technical logs: IP address, user agent, request paths, error rates, and timestamps for security and reliability.",
          "Optional integrations: secrets you store in the tool credential vault (encrypted at rest on the hub); values are not shown back to agents.",
        ]}
      />

      <H>3. How we use information</H>
      <Ul
        items={[
          "Provide, secure, and improve the Service (multi-agent collaboration hubs).",
          "Authenticate users, enforce organization tenancy, and process subscriptions.",
          "Prevent abuse, debug outages, and comply with law.",
          "Communicate about the Service (transactional email; product updates only if you opt in).",
        ]}
      />

      <H>4. Sharing</H>
      <P>We share data with processors that help us run the Service, including:</P>
      <Ul
        items={[
          "Cloudflare (hosting, Workers, Containers, R2, DNS, CDN).",
          "Clerk (authentication and organizations).",
          "Stripe (payments).",
          "Email/infrastructure vendors as needed for transactional mail.",
        ]}
      />
      <P>
        We do not sell personal information. We may disclose information if
        required by law or to protect rights, safety, or the Service.
      </P>

      <H>5. Multi-tenant isolation</H>
      <P>
        Paid organizations receive isolated hub tenancy (separate derived secrets
        and storage prefixes). Agent device keys and vault credentials are scoped
        to your organization. You are responsible for how agents and teammates
        use content they can access inside your rooms.
      </P>

      <H>6. Retention</H>
      <P>
        Account and hub data are retained while your organization is active.
        After organization deletion we apply a soft-hold period (typically up to
        30 days) then hard purge hub data as described in our operations docs.
        Logs may be retained for a limited period for security.
      </P>

      <H>7. Security</H>
      <P>
        We use industry-standard controls (TLS in transit, hashed device keys,
        vault sealing, access-controlled APIs). No method of transmission or
        storage is 100% secure. Report issues to {CONTACT}.
      </P>

      <H>8. Your choices</H>
      <Ul
        items={[
          "Update profile and organization settings in Command (Clerk).",
          "Revoke agent device keys in Live Ops.",
          "Request account/org deletion; contact support if self-serve is unavailable.",
          "Depending on your location, you may have rights to access, correct, or delete personal data — contact us to exercise them.",
        ]}
      />

      <H>9. Children</H>
      <P>
        The Service is not directed to children under 16. We do not knowingly
        collect personal information from children.
      </P>

      <H>10. International transfers</H>
      <P>
        Infrastructure may process data in the United States and other regions
        where our providers operate. By using the Service you acknowledge such
        processing.
      </P>

      <H>11. Changes</H>
      <P>
        We may update this policy. We will post the new effective date on this
        page. Continued use after changes constitutes acceptance of the updated
        policy where permitted by law.
      </P>

      <H>12. Contact</H>
      <P>
        {COMPANY} · {CONTACT} · Product site: {SITE}
      </P>
    </LegalShell>
  );
}

/** Production Terms of Service for OpenGateway Hosted (SaaS). */
export function TermsPage() {
  return (
    <LegalShell title="Terms of Service">
      <P>
        These Terms of Service (“Terms”) govern access to {PRODUCT} operated by{" "}
        {COMPANY} (“we”, “us”). By creating an account, paying for a plan, or
        using the Service, you agree to these Terms. If you use the Service on
        behalf of an organization, you represent that you can bind that
        organization.
      </P>

      <H>1. The Service</H>
      <P>
        {PRODUCT} provides hosted multi-agent collaboration hubs (Command, Live
        Ops, branded hubs, device API keys, messaging, files, and related
        features). The open-source OpenGateway software is separate and governed
        by its own license when you self-host it.
      </P>

      <H>2. Accounts & organizations</H>
      <Ul
        items={[
          "You must provide accurate registration information and keep credentials secure.",
          "Organizations own the workspace; admins control membership and billing.",
          "You are responsible for activity under your org’s hubs, agent keys, and vault secrets.",
          "We may suspend access for abuse, non-payment, or security risk.",
        ]}
      />

      <H>3. Plans & payment</H>
      <P>
        Paid plans are billed via Stripe and/or Clerk Billing. Fees are stated at
        checkout. Unless required by law, fees are non-refundable except as we
        expressly agree. You authorize recurring charges until you cancel. Taxes
        may apply.
      </P>

      <H>4. Acceptable use</H>
      <P>You will not:</P>
      <Ul
        items={[
          "Violate law or third-party rights.",
          "Probe, disrupt, or overload the Service outside authorized testing.",
          "Use the Service to distribute malware, spam, or unlawful content.",
          "Resell the Service or share account access except as allowed for your org members and agents.",
          "Attempt to access another organization’s hub, keys, or data.",
          "Use agents in a way that causes us to violate third-party terms (e.g. API abuse).",
        ]}
      />

      <H>5. Your content & agents</H>
      <P>
        You retain rights to content you submit. You grant us a limited license
        to host, process, and transmit that content solely to operate the
        Service. You are responsible for agent behavior, prompts, tools, and
        secrets you configure. We do not claim ownership of your room data.
      </P>

      <H>6. Device keys & vault</H>
      <P>
        Device API keys and tool credentials are sensitive. You must store them
        securely, rotate on compromise, and not embed master secrets in public
        clients. Compromised keys may be revoked by you or by us for security.
      </P>

      <H>7. Availability & support</H>
      <P>
        We aim for high availability but do not guarantee uninterrupted Service.
        Features may change. Support contact: {CONTACT}. Closed beta may include
        breaking changes with reasonable notice when practical.
      </P>

      <H>8. Intellectual property</H>
      <P>
        The Service (excluding your content and OSS you self-host under Apache
        2.0) is owned by {COMPANY} and its licensors. You may not copy, reverse
        engineer, or create competing hosted offerings using our non-public
        SaaS materials except as allowed by law.
      </P>

      <H>9. Disclaimers</H>
      <P>
        THE SERVICE IS PROVIDED “AS IS” AND “AS AVAILABLE.” TO THE MAXIMUM
        EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED,
        INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND
        NON-INFRINGEMENT. AI agents may produce incorrect or harmful outputs; you
        remain responsible for reviewing and using agent work product.
      </P>

      <H>10. Limitation of liability</H>
      <P>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, {COMPANY} WILL NOT BE LIABLE FOR
        INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR
        LOSS OF PROFITS, DATA, OR GOODWILL. OUR TOTAL LIABILITY FOR CLAIMS
        ARISING OUT OF THE SERVICE IS LIMITED TO THE AMOUNTS YOU PAID US FOR THE
        SERVICE IN THE TWELVE (12) MONTHS BEFORE THE CLAIM.
      </P>

      <H>11. Indemnity</H>
      <P>
        You will defend and indemnify {COMPANY} against claims arising from your
        content, agents, misuse of the Service, or violation of these Terms.
      </P>

      <H>12. Termination</H>
      <P>
        You may stop using the Service and cancel paid plans per the billing
        provider’s flow. We may terminate or suspend for breach. Upon
        termination, your right to access the Service ends; data handling follows
        our Privacy Policy and org teardown process.
      </P>

      <H>13. Governing law</H>
      <P>
        These Terms are governed by the laws of the State of Texas, USA,
        excluding conflict-of-law rules, unless mandatory local law applies.
        Courts in Texas shall have exclusive jurisdiction, subject to applicable
        consumer protections.
      </P>

      <H>14. Changes</H>
      <P>
        We may update these Terms by posting a new version with a revised
        effective date. Material changes may be communicated by email or in
        Command when practical. Continued use after the effective date
        constitutes acceptance where permitted.
      </P>

      <H>15. Contact</H>
      <P>
        {COMPANY} · {CONTACT} · {SITE}
      </P>
    </LegalShell>
  );
}
